// @spec SHL-STR-017
// @spec SHL-STR-019
'use client';

import { Command } from 'cmdk';
import { Building2, ChevronRight, User } from 'lucide-react';
import * as React from 'react';

import type { SearchResult } from '@/api/search';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

export interface SearchResultItemProps {
  /** The search result to display */
  result: SearchResult;
  /** Search query for text highlighting */
  query: string;
  /** Called when item is selected */
  onSelect: () => void;
  /** Called when item should open in new tab (Cmd/Ctrl + click or Cmd/Ctrl + Enter) */
  onSelectNewTab?: () => void;
}

/**
 * Get the appropriate Lucide icon component based on icon name.
 */
function getIconComponent(iconName: string) {
  switch (iconName) {
    case 'user':
      return User;
    case 'building-2':
      return Building2;
    default:
      return User;
  }
}

/**
 * Highlight matched text in a string.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query || !text) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return (
    <>
      {before}
      <mark className="rounded-sm bg-yellow-200 px-0.5 text-yellow-900 dark:bg-yellow-800 dark:text-yellow-100">
        {match}
      </mark>
      {after}
    </>
  );
}

/**
 * Get initials from a display name for avatar fallback.
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Individual search result item component.
 *
 * Renders a result within the search dropdown with:
 * - Icon or avatar on the left
 * - Title with query text highlighted
 * - Subtitle below title
 * - Chevron icon on the right
 *
 * Supports modifier key (Cmd/Ctrl) for opening in new tab.
 */
export function SearchResultItem({
  result,
  query,
  onSelect,
  onSelectNewTab,
}: SearchResultItemProps) {
  const IconComponent = getIconComponent(result.icon);
  const showAvatar = result.icon === 'user' && result.metadata.avatarUrl;

  // Handle selection with modifier key detection
  const handleSelect = React.useCallback(() => {
    // cmdk onSelect doesn't pass the event, so we check the last keyboard event
    // For keyboard-based selection, we track modifier state via onKeyDown
    onSelect();
  }, [onSelect]);

  // Handle click with modifier key detection
  const handleClick = React.useCallback(
    (event: React.MouseEvent) => {
      // Check for Cmd (Mac) or Ctrl (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && onSelectNewTab) {
        event.preventDefault();
        event.stopPropagation();
        onSelectNewTab();
      }
      // Normal click is handled by cmdk's onSelect
    },
    [onSelectNewTab]
  );

  // Handle keyboard with modifier key detection
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && onSelectNewTab) {
        event.preventDefault();
        event.stopPropagation();
        onSelectNewTab();
      }
      // Normal Enter is handled by cmdk's onSelect
    },
    [onSelectNewTab]
  );

  return (
    <Command.Item
      value={`${result.title}-${result.id}`}
      onSelect={handleSelect}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2',
        'text-sm aria-selected:bg-accent'
      )}
    >
      {/* Icon or Avatar */}
      {showAvatar && result.metadata.avatarUrl ? (
        <Avatar className="h-8 w-8">
          <AvatarImage src={result.metadata.avatarUrl} alt={result.title} />
          <AvatarFallback>{getInitials(result.title)}</AvatarFallback>
        </Avatar>
      ) : (
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
          <IconComponent className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium">{highlightMatch(result.title, query)}</span>
        {result.subtitle && (
          <span className="truncate text-xs text-muted-foreground">
            {highlightMatch(result.subtitle, query)}
          </span>
        )}
      </div>

      {/* Role badge for members */}
      {result.metadata.role && (
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          {result.metadata.role}
        </span>
      )}

      {/* Chevron */}
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-aria-selected:opacity-100" />
    </Command.Item>
  );
}
